<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import AcceptTerms from '@/Components/AcceptTerms.vue';
import CarTypes from '@/Components/CarTypes.vue';
import DateTimePicker from '@/Components/DateTimePicker.vue';
import ExtraProducts from '@/Components/ExtraProducts.vue';
import GoogleAutocomplete from '@/Components/GoogleAutocomplete.vue';
import { Head, router } from '@inertiajs/vue3';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
import { trans as $t } from 'laravel-vue-i18n';
</script>

<script>
const bootstrap = typeof window !== `undefined` && import('bootstrap');

export default {

    props: {
        bounds: {
            type: Object
        },
        carTypes: {
            type: Array
        },
        countries: {
            type: Array
        },
        currencies: {
            type: Array
        },
        currency: {
            type: String,
            required: true
        },
        errors: {
            type: Object
        },
        orderParams: {
            type: Object
        },
        paymentError: {
            type: String
        },
        price: {
            type: Object
        },
        productCategories: {
            type: Object
        },
        redirectUrl: {
            type: String
        },
        user: {
            type: Object
        }
    },

    data() {
        return {
            ajaxRequest: false,
            hasUserAccount: true,
            hasCoupon: false,
            orderData: {
                currency: '',
                carType: 'social',
                transferType: 'AC',
                tripType: 'return',
                selectedAddress: null,
                selectedAddressText: 'Budapest, ',
                adults: 1,
                children: 0,
                childSeatCount: 0,
                childSeat: {
                    baby: 0,
                    child: 0,
                    junior: 0
                },
                // extraSeat: 0,
                name: '',
                email: '',
                username: '',
                password: '',
                passwordRepeat: '',
                phone: '',
                couponCode: '',
                vrFlightNr: '',
                vrDate: '',
                vrTime: '',
                rvFlightNr: '',
                rvDate: '',
                rvTime: '',
                extraProducts: [],
                invoiceData: {
                    privatePerson: false,
                    name: '',
                    country: '',
                    postcode: '',
                    city: '',
                    address: '',
                    vatNr: '',
                },
                termsAccepted: false,
                totalPrice: {
                    HUF: 0,
                    EUR: 0,
                    details: null
                },
                paymentMethod: 'card'
            },
            steps: {
                transferDetails: false,
                carType: false,
                extraProducts: false,
                login: false,
                invoice: false
            },
            stepNrs: {
                transferDetails: 1,
                carType: 2,
                extraProducts: 3,
                login: 4,
                invoice: 5
            },
        }
    },

    methods: {

        toggleMobileSidebar() {
            this.$refs.bookingSidebarMobile.classList.toggle('expanded');
        },

        setAddress(newAddress, inputAddress) {
            this.orderData.selectedAddress = newAddress;
            this.orderData.selectedAddressText = inputAddress;
        },

        getCarType(typeName) {
            let carType;
            for (let i = 0; i < this.carTypes.length; i++) {
                carType = this.carTypes[i];
                if (carType.name == typeName) {
                    return carType;
                }
            }
        },

        getExtraProduct(productId) {
            let product;
            for (let i = 0; i < this.productCategories.length; i++) {
                for (let j = 0; j < this.productCategories[i].products.length; j++) {
                    product = this.productCategories[i].products[j];
                    if (product.id == productId) {
                        return product;
                    }
                }
            }
        },

        calculatePrice() {

            if (!this.orderData.selectedAddress?.placeId) {
                this.orderData.totalPrice = {
                    HUF: 0,
                    EUR: 0,
                    details: null
                }
                return;
            }

            router.post(route('calculatePrice'), this.orderData, {
                preserveState: true,
                preserveScroll: true,
                only: ['price', 'errors'],
                onStart: visit => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {
                    if (page.props?.price) {
                        this.orderData.totalPrice = page.props.price;
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                    this.orderData.totalPrice = {
                        HUF: 0,
                        EUR: 0,
                        details: null
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });
        },

        getTotalPrice(currency) {
            return new Intl.NumberFormat().format(currency == 'HUF' ? this.orderData.totalPrice.HUF : this.orderData.totalPrice.EUR) + ' ' + currency;
        },

        getCurrencyPrice(priceHuf, priceEur) {
            return new Intl.NumberFormat().format(this.orderData.currency == 'HUF' ? priceHuf : priceEur) + ' ' + this.orderData.currency;
        },

        getChildSeat() {
            return this.orderData.childSeat.baby + this.orderData.childSeat.child + this.orderData.childSeat.junior;
        },

        increaseChildSeat(type) {
            if (this.getChildSeat() < this.orderData.adults + this.orderData.children - 1) {
                this.orderData.childSeat[type] += 1;
            }
        },

        decreaseChildSeat(type) {
            if (this.orderData.childSeat[type] > 0) {
                this.orderData.childSeat[type] -= 1;
            }
        },

        // Open transfer details tab
        jumpToTransferDetails() {

            // Collapse.getOrCreateInstance('#collapseTransfer').show();
            bootstrap?.then(({ Collapse }) => {
                Collapse.getOrCreateInstance('#collapseTransfer').show();
            });

        },

        // Open car types tab
        jumpToCarTypes() {

            this.ajaxRequest = true;
            router.post(route('checkStep', { step: 'transferDetails' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    this.gtagTransfer();

                    if (dataLayer) {
                        dataLayer.push({
                            event: 'booking_step',
                            step_number: 1,
                            login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                        });
                    }

                    this.steps.transferDetails = true;

                    bootstrap?.then(({ Collapse }) => {
                        Collapse.getOrCreateInstance('#collapseCarType').show();
                    });

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        // Open extra products tab
        jumpToExtraProducts() {

            this.ajaxRequest = true;
            router.post(route('checkStep', { step: 'carType' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    if (dataLayer) {
                        dataLayer.push({
                            event: 'booking_step',
                            step_number: 2,
                            login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                            bus_type: this.orderData.carType,
                        });
                    }

                    this.steps.carType = true;

                    bootstrap?.then(({ Collapse }) => {
                        Collapse.getOrCreateInstance('#collapseExtraProducts').show();
                    });

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });
        },

        jumpToLogin() {

            this.errors.username = '';
            this.errors.password = '';

            this.ajaxRequest = true;
            router.post(route('checkStep', { step: 'extraProducts' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    this.gtagExtraProducts();

                    if (dataLayer) {
                        dataLayer.push({
                            event: 'booking_step',
                            step_number: 3,
                            login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                        });
                    }

                    this.steps.extraProducts = true;

                    if (this.user) {

                        this.steps.login = true;

                        this.jumpToInvoice();

                    } else {
                        bootstrap?.then(({ Collapse }) => {
                            Collapse.getOrCreateInstance('#collapseLogin').show();
                        });
                    }

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        jumpToInvoice() {

            // Already logged in, do nothing!
            if (this.user) {

                // GA4
                this.gtagBeginCheckout();

                if (dataLayer) {
                    dataLayer.push({
                        event: 'booking_step',
                        step_number: 4,
                        login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                    });
                }

                // this.steps.login = true;
                bootstrap?.then(({ Collapse }) => {
                    Collapse.getOrCreateInstance('#collapseInvoice').show();
                });
                return;
            }

            if (this.hasUserAccount) {
                // Login
                router.post(route('authenticate'), {
                    username: this.orderData.username,
                    password: this.orderData.password,
                    remember: true,
                    redirect: false
                }, {
                    only: ['user', 'errors'],
                    preserveState: true,
                    preserveScroll: true,
                    onStart: visit => {
                        this.ajaxRequest = true;
                    },
                    onSuccess: page => {

                        // console.log('Login success!');

                        // Check if user can order the selected car type
                        if (this.user.carTypes) {
                            if (!this.user.carTypes.includes(this.orderData.carType)) {
                                this.orderData.carType = this.user.carTypes[0];
                            }
                        }

                        // B2B users cannot order extra products, and does not have coupon
                        if (this.user.isB2B) {
                            this.orderData.extraProducts = [];
                            this.orderData.couponCode = '';
                            this.hasCoupon = false;
                        }

                        // Recalculate price
                        this.calculatePrice();

                        // GA4
                        this.gtagBeginCheckout();

                        if (dataLayer) {
                            dataLayer.push({
                                event: 'login',
                            });
                        }

                        if (dataLayer) {
                            dataLayer.push({
                                event: 'booking_step',
                                step_number: 4,
                                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                            });
                        }

                        this.steps.login = true;

                        // Load invoice details from user account
                        if (this.user.invoiceData?.name) {
                            this.orderData.invoiceData = this.user.invoiceData;
                        }

                        // Hotel user can order only to own address
                        if (this.user.isHotel) {

                            this.loadHotelAddress();

                            this.orderData.email = this.user.email;
                            this.orderData.phone = this.user.phone;
                        }

                        this.calculateSteps();

                        if (this.stepNrs.invoice) {
                            bootstrap?.then(({ Collapse }) => {
                                Collapse.getOrCreateInstance('#collapseInvoice').show();
                            });
                        }

                    },
                    onError: errors => {
                        if (errors.alert) {
                            Swal.fire({
                                icon: 'error',
                                text: errors.alert,
                            });
                        } else {
                            this.$utils.scrollToFirstError();
                        }
                    },
                    onFinish: visit => {
                        this.ajaxRequest = false;
                    },
                });
            } else {
                // Register
                router.post(route('register'), {
                    name: this.orderData.name,
                    email: this.orderData.email,
                    phone: this.orderData.phone,
                    username: this.orderData.username,
                    password: this.orderData.password,
                    passwordRepeat: this.orderData.passwordRepeat,
                    remember: true,
                    redirect: false
                }, {
                    only: ['user', 'errors'],
                    preserveState: true,
                    preserveScroll: true,
                    onStart: visit => {
                        this.ajaxRequest = true;
                    },
                    onSuccess: page => {

                        // console.log('Registration success!');

                        // Recalculate price
                        this.calculatePrice();

                        // GA4
                        this.gtagBeginCheckout();

                        if (dataLayer) {
                            dataLayer.push({
                                event: 'registration',
                                email: this.orderData.email,
                                phone: this.orderData.phone,
                            });
                        }

                        if (dataLayer) {
                            dataLayer.push({
                                event: 'booking_step',
                                step_number: 4,
                                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                            });
                        }

                        this.steps.login = true;

                        bootstrap?.then(({ Collapse }) => {
                            Collapse.getOrCreateInstance('#collapseInvoice').show();
                        });
                    },
                    onError: errors => {
                        if (errors.alert) {
                            Swal.fire({
                                icon: 'error',
                                text: errors.alert,
                            });
                        } else {
                            this.$utils.scrollToFirstError();
                        }
                    },
                    onFinish: visit => {
                        this.ajaxRequest = false;
                    },
                });
            }
        },

        checkInvoiceDetails(method) {

            this.orderData.paymentMethod = method;

            if (this.steps.invoice) {
                if (method == 'card') {
                    this.startPayment();
                } else {
                    this.savePayment();
                }
                return;
            }

            this.ajaxRequest = true;
            router.post(route('checkStep', { step: 'invoiceDetails' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    if (dataLayer) {
                        dataLayer.push({
                            event: 'booking_step',
                            step_number: 5,
                            login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                        });
                    }

                    this.steps.invoice = true;

                    if (method == 'card') {
                        this.startPayment();
                    } else {
                        this.savePayment();
                    }

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        startPayment() {

            this.ajaxRequest = true;
            router.post(route('startBookingPayment'), this.orderData, {
                only: ['redirectUrl', 'errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    this.gtagPaymentInfo();

                    // Wait a bit before redirection
                    setTimeout(() => {
                        window.location = page.props.redirectUrl;
                    }, 250);

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    setTimeout(() => {
                        this.ajaxRequest = false;
                    }, 1000);
                },
            });

        },

        savePayment() {

            this.ajaxRequest = true;
            router.post(route('saveBooking'), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // ...

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        // Show password reminder modal
        showReminderModal() {

            bootstrap?.then(({ Modal }) => {
                Modal.getOrCreateInstance('#reminderModal').show();
            });

        },

        // Send password reminder e-mail
        sendReminderMail() {

            this.ajaxRequest = true;
            router.post(route('doPasswordReminder'), {
                reminderusername: this.$refs.reminderUsername.value
            }, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                // preserveScroll: (page) => Object.keys(page.props.errors).length,
                preserveScroll: true,
                onSuccess: page => {

                    bootstrap?.then(({ Modal }) => {
                        Modal.getOrCreateInstance('#reminderModal').hide();
                    });

                    Swal.fire({
                        icon: 'success',
                        text: $t('A jelszó visszaállításához szükséges e-mailt elküldtük Önnek!'),
                    });
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } /*else {
                        this.$utils.scrollToFirstError();
                    }*/
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        // GA4 helper: get items
        gtagItems(includeTransfer, includeExtraProducts) {

            let items = [];

            const flightArrivalDate = (this.orderData.transferType == 'AC' || this.orderData.tripType == 'return') ? this.orderData.rvDate : '';
            const flightDepartureDate = (this.orderData.transferType == 'CA' || this.orderData.tripType == 'return') ? this.orderData.vrDate : '';

            if (includeTransfer) {

                const itemName = 'AIRPORT SHUTTLE | ' + (this.orderData.tripType == 'return' ? 'Return' : 'One-way') + ' | adult ' + this.orderData.adults + ' - underAge2 ' + this.orderData.children;

                items.push({
                    'item_name': itemName,
                    'item_id': itemName,
                    'price': this.orderData.currency == 'HUF' ? this.orderData.totalPrice.HUF : this.orderData.totalPrice.EUR,
                    'currency': this.orderData.currency,
                    'item_category': 'Transfer shuttle',
                    'item_list_name': 'Transfer shuttle type',
                    'index': 1,
                    'quantity': '1',
                    'transfer_type': (this.orderData.tripType == 'return' ? 'Return' : 'One-way') + ' transfer',
                    'transfer_direction': this.orderData.transferType == 'AC' ? 'Airport to City' : 'City to Airport',
                    'adult_passangers_number': this.orderData.adults,
                    'passangers_underage2_number': this.orderData.children,
                    'flight_arriving_date': flightArrivalDate,
                    'flight_departure_date': flightDepartureDate,
                });

            }

            if (includeExtraProducts) {

                let product;
                for (let item of this.orderData.extraProducts) {
                    product = this.getExtraProduct(item.id);
                    if (product) {
                        items.push({
                            'item_name': product.name,
                            'item_id': product.name,
                            'price': (this.orderData.currency == 'HUF' ? product.price.HUF : product.price.EUR),
                            'currency': this.orderData.currency,
                            'item_category': 'Extra product',
                            'quantity': item.quantity,
                            'transfer_type': (this.orderData.tripType == 'return' ? 'Return' : 'One-way') + ' transfer',
                            'transfer_direction': this.orderData.transferType == 'AC' ? 'Airport to City' : 'City to Airport',
                            'adult_passangers_number': this.orderData.adults,
                            'passangers_underage2_number': this.orderData.children,
                            'flight_arriving_date': flightArrivalDate,
                            'flight_departure_date': flightDepartureDate,
                        });
                    }
                }
            }

            // console.log(items);

            return items;

        },

        // GA4: add_to_cart event when jumping from tranfer details to car types
        gtagTransfer() {

            let items = [];

            dataLayer.push({
                'event': 'add_to_cart',
                'ecommerce': {
                    'items': this.gtagItems(true, false)
                }
            });
        },

        // GA4: add_to_cart event when jumping from extra products to login / invoice details
        gtagExtraProducts() {

            if (!this.orderData.extraProducts.length) {
                return;
            }

            dataLayer.push({
                'event': 'add_to_cart',
                'ecommerce': {
                    'items': this.gtagItems(false, true)
                }
            });

        },

        // GA4: begin_checkout event when arriving to invoice details
        gtagBeginCheckout() {

            dataLayer.push({
                'event': 'begin_checkout',
                'ecommerce': {
                    'items': this.gtagItems(true, true)
                }
            });

        },

        // GA4: add_payment_info when
        gtagPaymentInfo() {

            dataLayer.push({
                'event': 'add_payment_info',
                'ecommerce': {
                    'first_name': this.orderData.name,
                    'last_name': '',
                    'email': this.orderData.email,
                    'phone': this.orderData.phone,
                    'postcode': this.orderData.invoiceData.postcode,
                    'country_code': this.orderData.invoiceData.country,
                    'city': this.orderData.invoiceData.city,
                    'street': this.orderData.invoiceData.address,
                    'payment_type': 'Credit card',
                    'items': this.gtagItems(true, true)
                }
            });

        },

        cityFromPostcode() {
            if (!this.orderData.invoiceData.postcode || this.orderData.invoiceData.city) {
                return;
            }

            const that = this;

            axios.get(route('cityFromPostcode'), {
                params: {
                    postcode: that.orderData.invoiceData.postcode
                }
            }).then(function (response) {
                if (response.data?.city) {
                    that.orderData.invoiceData.city = response.data.city;
                }
            });
        },

        postcodeFromCity() {
            if (!this.orderData.invoiceData.city || this.orderData.invoiceData.postcode) {
                return;
            }

            const that = this;

            axios.get(route('postcodeFromCity'), {
                params: {
                    city: that.orderData.invoiceData.city
                }
            }).then(function (response) {
                if (response.data?.postcode) {
                    that.orderData.invoiceData.postcode = response.data.postcode;
                }
            });
        },

        fillUserFields() {

            if (!this.user) {
                return;
            }

            this.steps.login = true;

            // Hotel user can order only to own address
            if (this.user.isHotel) {
                this.loadHotelAddress();
            }

            // Fill name and email for everyone, these will be read-only to hotel users
            this.orderData.email = this.user.email;
            this.orderData.phone = this.user.phone;

            // Fill name field for regular users
            if (!this.user.isB2B) {
                this.orderData.name = this.user.name;
            }

            // B2B users do not require flight numbers
            if (this.user.isB2B) {
                this.orderData.vrFlightNr = '-';
                this.orderData.rvFlightNr = '-';
            }

            // Fill invoice details
            if (this.user.invoiceData?.name) {
                // console.log(this.user.invoiceData);
                this.orderData.invoiceData = this.user.invoiceData;
            }

        },

        loadHotelAddress() {
            this.orderData.selectedAddress = {
                placeId: '-',
                displayName: this.user.displayName,
                country: 'Magyarország',
                district: '-',
                postcode: this.user.postcode,
                city: this.user.city,
                street: this.user.street,
                houseNr: this.user.houseNr,
                poi: true,
                latitude: this.user.latitude,
                longitude: this.user.longitude
            }
            this.orderData.selectedAddressText = this.user.displayName;
        },

        // Recalculate step numbers
        calculateSteps() {

            let lastStep = 0;

            this.stepNrs.transferDetails = ++lastStep;

            this.stepNrs.carType = ++lastStep;

            if (this.user?.isB2B) {
                this.stepNrs.extraProducts = 0;
            } else {
                this.stepNrs.extraProducts = ++lastStep;
            }

            if (this.user) {
                this.stepNrs.login = 0;
            } else {
                this.stepNrs.login = ++lastStep;
            }

            if (this.user?.isB2B) {
                this.stepNrs.invoice = 0;
            } else {
                this.stepNrs.invoice = ++lastStep;
            }

        },

        getNameFieldLabel() {
            if (this.user?.isHotel) {
                return $t('Vendég neve') + ' / ' + $t('Szobaszám');
            } else if (this.user?.isPartner) {
                return $t('Utas neve');
            } else {
                return $t('Név');
            }
        },

        getPhoneFieldLabel() {
            if (this.user?.isPartner) {
                return $t('Utas mobiltelefonszáma');
            } else {
                return $t('Mobiltelefonszám');
            }
        }

    },

    watch: {

        hasUserAccount: {
            handler(newValue, oldValue) {
                // Reset error messages on login / register tab switch
                this.errors.username = '';
                this.errors.password = '';
            },
        },

        'orderData.selectedAddress': {
            handler(newValue, oldValue) {
                // console.log('Address changed, calculate price!');
                this.calculatePrice();
            },
            deep: true,
            // immediate: true,
        },

        'orderData.tripType': {
            handler(newValue, oldValue) {
                // console.log('Trip type changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.adults': {
            handler(newValue, oldValue) {
                // console.log('Nr of adults changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.children': {
            handler(newValue, oldValue) {
                // console.log('Nr of children changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.couponCode': {
            handler(newValue, oldValue) {
                // console.log('Coupon code changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.carType': {
            handler(newValue, oldValue) {
                // console.log('Car type changed, calculate price!');
                this.calculatePrice();
            },
        },

        /*
        'orderData.vrDate': {
            handler(newValue, oldValue) {
                // console.log('V-R date changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.vrTime': {
            handler(newValue, oldValue) {
                // console.log('V-R time changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.rvDate': {
            handler(newValue, oldValue) {
                // console.log('R-V date changed, calculate price!');
                this.calculatePrice();
            },
        },

        'orderData.rvTime': {
            handler(newValue, oldValue) {
                // console.log('R-V time changed, calculate price!');
                this.calculatePrice();
            },
        },
        */

        'orderData.extraProducts': {
            handler(newValue, oldValue) {
                // console.log('Extra products changed, calculate price!');
                this.calculatePrice();
            },
            deep: true
        },

        'orderData.childSeat': {
            handler(newValue, oldValue) {
                // Refresh child seat total count when child seat properties change
                this.orderData.childSeatCount = this.getChildSeat();
            },
            deep: true,
            // immediate: true,
        },

        'orderData.email': {
            handler(newValue, oldValue) {
                // Fill username on email change
                this.orderData.username = this.orderData.email;
            },
        },

        /*
        'orderData.phone': {
            handler(newValue, oldValue) {
                console.log('Phone changed', newValue);
            },
        },
        */

    },

    beforeMount() {

        // Fill order data with optional order parameters
        if (this.orderParams) {
            this.orderData = { ...this.orderData, ...this.orderParams };
        }

        // Fill user fields
        this.fillUserFields();

    },

    mounted() {

        if (this.orderParams) {
            this.orderData = { ...this.orderData, ...this.orderParams };
        }

        this.fillUserFields();

        if (this.price) {
            this.orderData.totalPrice = this.price;
        }

        this.orderData.currency = this.currency;

        if (this.orderData.couponCode) {
            this.hasCoupon = true;
        }

        if (this.paymentError) {
            this.steps.transferDetails = true;
            this.steps.carType = true;
            this.steps.extraProducts = true;
            this.steps.login = true;
            this.steps.invoice = true;
        }

        // Scroll to accordion item on step change
        const accordion = document.getElementById('bookingAccordion');
        const accordionItems = accordion.querySelectorAll('.accordion-item');
        accordionItems.forEach(function (elem) {
            elem.addEventListener('shown.bs.collapse', event => {
                if (event.target) {
                    const headerOffset = event.target.getBoundingClientRect().top;
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    const targetOffset = headerOffset + scrollTop - 150;

                    window.scrollTo({
                        top: targetOffset,
                        behavior: 'smooth'
                    });
                }
            });
        });

        this.calculateSteps();

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'booking',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });

            dataLayer.push({
                event: 'booking_step',
                step_number: 0,
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
            });
        }

    }

}

</script>

<style>
.collapsing {
    -webkit-transition: none;
    transition: none;
}

.input-group .input-group-append .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.modal-open {
    overflow: auto;
    padding-right: 0 !important;
}
</style>

<template>

    <Head :title="$t('Reptéri transzfer foglalás')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container-fluid">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('Reptéri transzfer foglalás') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!--<div class="header-placeholder"></div>-->

        <div class="subpage-content">

            <div class="container subpage-container">
                <div class="row">

                    <aside class="booking-sidebar col-12 col-md-4 col-lg-3 order-md-1 mb-3 mb-md-0 d-none d-md-block">
                        <div class="card text-bg-secondary sticky-top">
                            <div class="card-body">
                                <div class="cart-icon">
                                    <i class="far fa-shopping-cart"></i>
                                </div>
                                <div class="text-center mb-2">
                                    <img src="../../images/logo.png" alt="miniBUD" width="133" height="32">
                                </div>
                                <div class="cart-headline">{{ $t('Foglalás összesítő') }}</div>
                                <div class="row form-row align-items-center" v-if="currencies.length > 1">
                                    <div class="col-6 text-uppercase text-bold text-left">{{ $t('Pénznem') }}</div>
                                    <div class="col-6">
                                        <ul class="booking-currency-list">
                                            <li v-for="c in currencies" class="booking-currency-list-item" @click="orderData.currency = c" :class="{ 'current': orderData.currency == c }">
                                                {{ c }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="cart-steps">
                                    <div class="cart-step">
                                        <ul class="step-list">
                                            <li class="step-list-item">
                                                <span>{{ $t('Típus') }}:</span>
                                                {{ orderData.tripType == 'oneWay' ? $t('Egyirányú transzfer') : $t('Retúr transzfer') }}
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Transzfer') }}:</span>
                                                {{ orderData.transferType == 'AC' ? $t('Reptérről a városba') : $t('Városból a reptérre') }}
                                            </li>
                                            <li class="step-list-item" v-if="orderData.selectedAddress?.placeId">
                                                <span>{{ $t('Cím') }}:</span>
                                                {{ orderData.selectedAddressText }}
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Utasok') }}:</span>
                                                <p class="m-0">{{ $t('Felnőtt utas') }}: {{ orderData.adults }}</p>
                                                <p class="m-0" v-if="orderData.children">{{ $t('2 éven aluli utas') }}: {{ orderData.children }}</p>
                                                <p class="m-0" v-if="orderData.childSeatCount">{{ $t('Gyerekülés') }}: {{ orderData.childSeatCount }}</p>
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Jármű típus') }}</span>
                                                <p class="m-0">{{ $t(getCarType(orderData.carType)?.properties.displayName) }}</p>
                                            </li>
                                            <li class="step-list-item" v-if="orderData.extraProducts.length">
                                                <span>{{ $t('Extra szolgáltatások') }}</span>
                                                <p class="m-0" v-for="product in orderData.extraProducts">{{ product.quantity }}x {{ getExtraProduct(product.id)?.name }}</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <template v-if="orderData.totalPrice.HUF">
                                    <div class="cart-separator"></div>
                                    <div class="cart-headline">{{ $t('Összesen') }}</div>
                                    <div class="cart-total">{{ getTotalPrice(orderData.currency) }}</div>
                                </template>
                            </div>
                        </div>
                    </aside>

                    <main class="main col-12 col-md-8 col-lg-9 order-md-2 mb-3 mb-md-0">
                        <section>
                            <form autocomplete="off">

                                <div class="alert alert-danger d-flex align-items-center" role="alert" v-if="paymentError">
                                    <i class="fas fa-exclamation-circle me-2" role="img" aria-label="Danger:"></i>
                                    <div><strong>{{ $t('Sikertelen tranzakció!') }}</strong> {{ paymentError }}</div>
                                </div>

                                <div class="accordion" id="bookingAccordion">

                                    <!-- Tranfer details -->
                                    <div class="accordion-item" v-if="stepNrs.transferDetails">
                                        <div class="accordion-header">
                                            <button class="accordion-button" :class="{ 'collapsed': paymentError }" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTransfer" aria-controls="collapseTransfer" :aria-expanded="paymentError ? false : true">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.transferDetails, 'text-success fa-check-circle': steps.transferDetails }"></i>{{
                                                        stepNrs.transferDetails }}.
                                                    {{ $t('Transzfer foglalás opciók') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseTransfer" class="accordion-collapse collapse" :class="{ 'show': !paymentError }" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <p class="fw-bold mb-3">{{ $t('Transzfer típusa') }}<span class="text-danger">*</span>
                                                        </p>
                                                        <div class="btn-group btn-group-primary w-100 mb-3" role="group" :aria-label="$t('Transzfer típusa')">
                                                            <input type="radio" class="btn-check" name="tripType" id="return" value="return" v-model="orderData.tripType" autocomplete="off" />
                                                            <label class="btn btn-outline-primary w-100" for="return">
                                                                <span><i class="fal fa-exchange me-1"></i></span>
                                                                <span>{{ $t('Retúr transzfer') }}</span>
                                                            </label>
                                                            <input type="radio" class="btn-check" name="tripType" id="oneway" value="oneWay" v-model="orderData.tripType" autocomplete="off" />
                                                            <label class="btn btn-outline-primary w-100" for="oneway">
                                                                <span><i class="fal fa-long-arrow-right me-1"></i></span>
                                                                <span>{{ $t('Egyirányú transzfer') }}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col-12">
                                                        <p class="fw-bold mb-3">
                                                            <span v-if="orderData.tripType == 'return'">{{ $t('Kérjük adja meg az első transzfer irányát!') }}</span>
                                                            <span v-else>{{ $t('Kérjük adja meg a transzfer irányát!') }}</span>
                                                            <span class="text-danger">*</span>
                                                        </p>
                                                        <div class="btn-group btn-group-primary w-100 mb-3" role="group" :aria-label="$t('Transzfer iránya')">
                                                            <input type="radio" class="btn-check" name="transferType" id="airport-city" value="AC" v-model="orderData.transferType" autocomplete="off" />
                                                            <label class="btn btn-outline-primary w-100" for="airport-city">
                                                                <span class="mb-2">
                                                                    <i class="fal fa-plane-arrival me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-city me-1"></i>
                                                                </span>
                                                                <span>{{ $t('Reptérről a városba') }}</span>
                                                            </label>
                                                            <input type="radio" class="btn-check" name="transferType" id="city-airport" value="CA" v-model="orderData.transferType" autocomplete="off" />
                                                            <label class="btn btn-outline-primary w-100" for="city-airport">
                                                                <span class="mb-2">
                                                                    <i class="fal fa-city me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-plane-departure me-1"></i>
                                                                </span>
                                                                <span>{{ $t('Városból a reptérre') }}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col-12">
                                                        <div class="form-floating mb-3">
                                                            <template v-if="user?.isHotel">
                                                                <input type="text" class="form-control" :class="{ 'is-invalid': errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }" id="address" :placeholder="$t('Hotel')" v-model="orderData.selectedAddressText" readonly autocomplete="off">
                                                                <label for="name">{{ $t('Hotel') }}<span class="text-danger">*</span></label>
                                                                <div class="invalid-feedback" v-if="errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName']">{{
                                                                    errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }}</div>
                                                            </template>
                                                            <template v-else>
                                                                <GoogleAutocomplete id="address" :placeholder="$t('Hotel vagy cím')" class="form-control" :class="{ 'is-invalid': errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }" :bounds-north="bounds.north" :bounds-east="bounds.east" :bounds-south="bounds.south" :bounds-west="bounds.west" :value="orderData.selectedAddressText" @address-changed="setAddress" />
                                                                <div class="invalid-feedback" v-if="errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName']">
                                                                    {{ errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }}
                                                                </div>
                                                            </template>
                                                        </div>
                                                    </div>
                                                    <div class="col-6 col-lg-4">
                                                        <div class="form-floating mb-3">
                                                            <input type="number" class="form-control" :class="{ 'is-invalid': errors.adults }" id="adults" :placeholder="$t('Felnőtt utas')" min="1" max="99" v-model="orderData.adults">
                                                            <label for="adults">{{ $t('Felnőtt utas') }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.adults">{{ errors.adults }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-6 col-lg-4">
                                                        <div class="form-floating mb-3">
                                                            <input type="number" class="form-control" :class="{ 'is-invalid': errors.children }" id="children" :placeholder="$t('2 éven aluli utas')" min="0" max="99" v-model="orderData.children">
                                                            <label for="children">{{ $t('2 éven aluli utas') }}</label>
                                                            <div class="invalid-feedback" v-if="errors.children">{{ errors.children }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-lg-4">
                                                        <div class="form-floating mb-3">
                                                            <input type="number" class="form-control" id="childseat" :placeholder="$t('Gyerekülés')" v-model="orderData.childSeatCount" readonly data-bs-toggle="dropdown" data-bs-auto-close="outside">
                                                            <div class="dropdown-menu dropdown-menu-end" id="childSeatDropdown">
                                                                <div class="booking-card booking-card-landscape">
                                                                    <div>
                                                                        <picture>
                                                                            <source srcset="../../images/childseat-1.webp" type="image/webp">
                                                                            <source srcset="../../images/childseat-1.jpg" type="image/png">
                                                                            <img src="../../images/childseat-1.jpg" width="60" height="60" class="_img-fluid booking-card-image" :alt="$t('Baby')" loading="lazy">
                                                                        </picture>
                                                                    </div>
                                                                    <div class="mx-2">
                                                                        <div class="booking-card-title text-nowrap">{{ $t('Baba hordozó') }}</div>
                                                                        <div class="booking-card-body">{{ $t('0-13 kg') }}</div>
                                                                    </div>
                                                                    <div class="me-2">
                                                                        <div class="input-number-group">
                                                                            <div><button type="button" class="input-number-action input-number-action-decrease" @click="decreaseChildSeat('baby')">-</button></div>
                                                                            <div class="input-number-value" :class="{ 'input-number-value-set': orderData.childSeat.baby > 0 }">{{
                                                                                orderData.childSeat.baby }} db</div>
                                                                            <div><button type="button" class="input-number-action input-number-action-increase" @click="increaseChildSeat('baby')">+</button></div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('Újszülött babáknak, ameddig nem tud ülni, vagy maximum 13 kg-ig. Isofix rögzítés, belső 3 pontos, központi övfeszítős biztonsági övvel köthető be a baba, menetiránnyal ellentétesen is használható.')"></i>
                                                                    </div>
                                                                </div>
                                                                <div class="booking-card booking-card-landscape">
                                                                    <div>
                                                                        <picture>
                                                                            <source srcset="../../images/childseat-2.webp" type="image/webp">
                                                                            <source srcset="../../images/childseat-2.jpg" type="image/png">
                                                                            <img src="../../images/childseat-2.jpg" width="60" height="60" class="_img-fluid booking-card-image" :alt="$t('Gyerek')" loading="lazy">
                                                                        </picture>
                                                                    </div>
                                                                    <div class="mx-2">
                                                                        <div class="booking-card-title text-nowrap">{{ $t('Kisgyerekülés') }}</div>
                                                                        <div class="booking-card-body">{{ $t('9-15 kg') }}</div>
                                                                    </div>
                                                                    <div class="me-2">
                                                                        <div class="input-number-group">
                                                                            <div><button type="button" class="input-number-action input-number-action-decrease" @click="decreaseChildSeat('child')">-</button></div>
                                                                            <div class="input-number-value" :class="{ 'input-number-value-set': orderData.childSeat.child > 0 }">{{
                                                                                orderData.childSeat.child }} db</div>
                                                                            <div><button type="button" class="input-number-action input-number-action-increase" @click="increaseChildSeat('child')">+</button></div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('Ülni tud a gyermek, 9 kg - 15 kg között használható. Isofix rögzítés, a védőpajzs előtt elvezetve az autó 3 pontos biztonsági övével köthető be.')"></i>
                                                                    </div>
                                                                </div>
                                                                <div class="booking-card booking-card-landscape">
                                                                    <div>
                                                                        <picture>
                                                                            <source srcset="../../images/childseat-3.webp" type="image/webp">
                                                                            <source srcset="../../images/childseat-3.jpg" type="image/png">
                                                                            <img src="../../images/childseat-3.jpg" width="60" height="60" class="_img-fluid booking-card-image" :alt="$t('Junior')" loading="lazy">
                                                                        </picture>
                                                                    </div>
                                                                    <div class="mx-2">
                                                                        <div class="booking-card-title text-nowrap">{{ $t('Gyerekülés') }}</div>
                                                                        <div class="booking-card-body">{{ $t('15-36 kg') }}</div>
                                                                    </div>
                                                                    <div class="me-2">
                                                                        <div class="input-number-group">
                                                                            <div><button type="button" class="input-number-action input-number-action-decrease" @click="decreaseChildSeat('junior')">-</button></div>
                                                                            <div class="input-number-value" :class="{ 'input-number-value-set': orderData.childSeat.junior > 0 }">{{
                                                                                orderData.childSeat.junior }} db</div>
                                                                            <div><button type="button" class="input-number-action input-number-action-increase" @click="increaseChildSeat('junior')">+</button></div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('15 kg-tól 36 kg-ig használható. Isofix rögzítés, az autó 3 pontos biztonsági övét a gyermek előtt elvezetve köthető be.')"></i>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <label for="childseat">{{ $t('Gyerekülés') }}</label>
                                                        </div>
                                                    </div>
                                                    <!--
                                                    <div class="col-6 col-lg-3">
                                                        <div class="form-floating mb-3">
                                                            <input type="number" class="form-control" id="extraseat" :placeholder="$t('Extra ülések')" min="0" max="99"
                                                                v-model="orderData.extraSeat">
                                                            <label for="extraseat">{{ $t('Extra ülések') }}</label>
                                                        </div>
                                                    </div>
                                                    -->
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="getNameFieldLabel()" v-model="orderData.name" maxlength="150" autocomplete="name">
                                                            <label for="name">{{ getNameFieldLabel() }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" v-model="orderData.email" maxlength="150" :readonly="user?.isHotel" autocomplete="email">
                                                            <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="getPhoneFieldLabel()" v-model="orderData.phone" :readonly="user?.isHotel" />
                                                            <label for="phone">{{ getPhoneFieldLabel() }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-6 d-flex flex-column justify-content-center" v-if="!user?.isB2B">
                                                        <div v-if="!hasCoupon" class="form-check form-switch mb-3">
                                                            <input class="form-check-input" type="checkbox" role="switch" id="hasCoupon" v-model="hasCoupon">
                                                            <label class="form-check-label" for="hasCoupon">{{ $t('Van kuponkódom') }}</label>
                                                        </div>
                                                        <div v-if="hasCoupon" class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors.couponCode }" id="coupon" :placeholder="$t('Kuponkód') + '(' + $t('ha van') + ')'" v-model="orderData.couponCode" maxlength="20">
                                                            <label for="coupon">{{ $t('Kuponkód') }} ({{ $t('ha van') }})</label>
                                                            <div class="invalid-feedback" v-if="errors.couponCode">{{ errors.couponCode }}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <hr />

                                                <template v-if="orderData.transferType == 'CA'">
                                                    <div class="row section-title">
                                                        <div class="col-12">
                                                            <div class="d-flex justify-content-between align-items-center">
                                                                <h3 class="my-3"><i class="fal fa-city me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-plane-departure me-1"></i>{{ $t('Első út') }} / {{ $t('Transzfer városból a reptérre') }}</h3>
                                                                <!-- <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('Lorem ipsum dolor sit amet')"></i> -->
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="row">
                                                        <div class="col-md-4" v-if="!user?.isB2B">
                                                            <div class="form-floating mb-3">
                                                                <input type="text" class="form-control text-uppercase" :class="{ 'is-invalid': errors.vrFlightNr }" id="vrFlightNr" :placeholder="$t('Induló repülő járatszáma')" maxlength="10" v-model="orderData.vrFlightNr">
                                                                <label for="vrFlightNr">{{ $t('Induló repülő járatszáma') }}<span class="text-danger">*</span></label>
                                                                <div class="invalid-feedback" v-if="errors.vrFlightNr">{{ errors.vrFlightNr }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="vrDate" :placeholder="$t('Repülő indulás dátuma')" class="form-control" :class="{ 'is-invalid': errors.vrDate }" :min-date="new Date()" picker-type="calendar" picker-format="L" :required="true" v-model="orderData.vrDate" />
                                                                <div class="invalid-feedback" v-if="errors.vrDate">{{ errors.vrDate }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="vrTime" :placeholder="$t('Repülő indulás időpontja')" class="form-control" :class="{ 'is-invalid': errors.vrTime }" picker-type="clock" picker-format="HH:mm" :required="true" v-model="orderData.vrTime" />
                                                                <div class="invalid-feedback" v-if="errors.vrTime">{{ errors.vrTime }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>

                                                <template v-if="orderData.transferType == 'AC' || orderData.tripType == 'return'">
                                                    <div class="row section-title">
                                                        <div class="col-12">
                                                            <div class="d-flex justify-content-between align-items-center">
                                                                <h3 class="my-3"><i class="fal fa-plane-arrival me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-city me-1"></i> {{ orderData.transferType == 'CA' ? $t('Második út') : $t('Első út') }} / {{
                                                                    $t('Transzfer reptérről a városba') }}</h3>
                                                                <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('A landolást vagy a csomagfelvételt követően reptéri pultunknál jelentkezzen be és aktiválja foglalását az utazáshoz.')"></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="row">
                                                        <div class="col-md-4" v-if="!user?.isB2B">
                                                            <div class="form-floating mb-3">
                                                                <input type="text" class="form-control text-uppercase" :class="{ 'is-invalid': errors.rvFlightNr }" id="rvFlightNr" :placeholder="$t('Érkező repülő járatszáma')" maxlength="10" v-model="orderData.rvFlightNr">
                                                                <label for="rvFlightNr">{{ $t('Érkező repülő járatszáma') }}<span class="text-danger">*</span></label>
                                                                <div class="invalid-feedback" v-if="errors.rvFlightNr">{{ errors.rvFlightNr }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="rvDate" :placeholder="$t('Érkezés dátuma')" :class="{ 'form-control': true, 'is-invalid': errors.rvDate }" :min-date="new Date()" picker-type="calendar" picker-format="L" :required="true" v-model="orderData.rvDate" />
                                                                <div class="invalid-feedback" v-if="errors.rvDate">{{ errors.rvDate }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="rvTime" :placeholder="$t('Érkezés időpontja')" :class="{ 'form-control': true, 'is-invalid': errors.rvTime }" picker-type="clock" picker-format="HH:mm" :required="true" v-model="orderData.rvTime" />
                                                                <div class="invalid-feedback" v-if="errors.rvTime">{{ errors.rvTime }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>

                                                <template v-if="orderData.transferType == 'AC' && orderData.tripType == 'return'">
                                                    <div class="row section-title">
                                                        <div class="col-12">
                                                            <div class="d-flex justify-content-between align-items-center">
                                                                <h3 class="my-3"><i class="fal fa-city me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-plane-departure me-1"></i>{{ $t('Második út') }} / {{ $t('Transzfer városból a reptérre') }}</h3>
                                                                <i class="fas fa-2x fa-info-circle text-primary" data-bs-toggle="tooltip" :title="$t('A repülőgép indulási időpontját adja meg, a pontos felvételi időpontról e-mail üzenetben értesítjük.')"></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="row">
                                                        <div class="col-md-4" v-if="!user?.isB2B">
                                                            <div class="form-floating mb-3">
                                                                <input type="text" class="form-control text-uppercase" :class="{ 'is-invalid': errors.vrFlightNr }" id="vrFlightNr" :placeholder="$t('Induló repülő járatszáma')" maxlength="10" v-model="orderData.vrFlightNr">
                                                                <label for="vrFlightNr">{{ $t('Induló repülő járatszáma') }}<span class="text-danger">*</span></label>
                                                                <div class="invalid-feedback" v-if="errors.vrFlightNr">{{ errors.vrFlightNr }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="vrDate" :placeholder="$t('Repülő indulás dátuma')" class="form-control" :class="{ 'is-invalid': errors.vrDate }" :min-date="new Date()" picker-type="calendar" picker-format="L" :required="true" v-model="orderData.vrDate" />
                                                                <div class="invalid-feedback" v-if="errors.vrDate">{{ errors.vrDate }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="col-md-4">
                                                            <div class="form-floating mb-3">
                                                                <DateTimePicker id="vrTime" :placeholder="$t('Repülő indulás időpontja')" class="form-control" :class="{ 'is-invalid': errors.vrTime }" picker-type="clock" picker-format="HH:mm" :required="true" v-model="orderData.vrTime" />
                                                                <div class="invalid-feedback" v-if="errors.vrTime">{{ errors.vrTime }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>

                                                <button type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToCarTypes()">{{ $t('Következő')
                                                }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Transfer details -->

                                    <!-- Car type -->
                                    <div class="accordion-item" v-if="stepNrs.carType">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseCarType" aria-controls="collapseCarType" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.transferDetails }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.carType, 'text-success fa-check-circle': steps.carType }"></i>{{
                                                        stepNrs.carType }}. {{ $t('Jármű típus kiválasztása') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseCarType" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <CarTypes :carTypes="carTypes" :modelValue="orderData.carType" @update:modelValue="newValue => orderData.carType = newValue" />
                                                <button v-if="stepNrs.extraProducts" type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToExtraProducts">{{ $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Car type -->

                                    <!-- Extra products -->
                                    <div class="accordion-item" v-if="stepNrs.extraProducts">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseExtraProducts" aria-controls="collapseExtraProducts" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.carType }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.extraProducts, 'text-success fa-check-circle': steps.extraProducts }"></i>{{
                                                        stepNrs.extraProducts }}.
                                                    {{ $t('Extra szolgáltatások') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseExtraProducts" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <ExtraProducts :currency="orderData.currency" :productCategories="productCategories" :modelValue="orderData.extraProducts" @update:modelValue="newValue => orderData.extraProducts = newValue" :transferType="orderData.transferType" :tripType="orderData.tripType" />
                                                <div v-if="errors.extraProducts" class="alert alert-danger fw-bold">{{ errors.extraProducts }}</div>
                                                <button type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToLogin">{{
                                                    $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Extra products -->

                                    <!-- Login or registration -->
                                    <div class="accordion-item" v-if="stepNrs.login">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseLogin" aria-controls="collapseLogin" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.extraProducts }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.login, 'text-success fa-check-circle': steps.login }"></i>{{
                                                        stepNrs.login }}. {{ $t('Felhasználói fiók') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseLogin" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">

                                                <div v-if="!user">
                                                    <div class="col-12">
                                                        <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Bejelentkezés')">
                                                            <input type="radio" class="btn-check" name="hasUserAccount" id="acc-yes" autocomplete="off" :value="true" v-model="hasUserAccount">
                                                            <label class="btn btn-outline-secondary w-100" for="acc-yes"><i class="fal fa-user-check me-1"></i>
                                                                {{ $t('Már van miniBUD fiókom') }}</label>
                                                            <input type="radio" class="btn-check" name="hasUserAccount" id="acc-no" autocomplete="off" :value="false" v-model="hasUserAccount">
                                                            <label class="btn btn-outline-secondary w-100" for="acc-no"><i class="fal fa-user-plus me-1"></i> {{
                                                                $t('Még nincs miniBUD fiókom') }}</label>
                                                        </div>
                                                    </div>
                                                    <div v-if="hasUserAccount" class="col-12">
                                                        <p class="fs-6" v-html="$t('Ha már van miniBUD fiókja, kérjük, felhasználói nevével és jelszavával jelentkezzen be, majd kattintson a Következő gombra!')">
                                                        </p>
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" id="username" name="username" :placeholder="$t('Felhasználói név / e-mail cím')" :class="{ 'is-invalid': errors.username }" v-model="orderData.username" maxlength="150" autocomplete="username">
                                                            <label for="username">{{ $t('Felhasználói név') }} / {{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="loginPassword" :placeholder="$t('Jelszó')" :class="{ 'is-invalid': errors.password }" v-model="orderData.password" maxlength="50" autocomplete="current-password">
                                                            <label for="loginPassword">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                        </div>
                                                        <div class="mb-3 text-center">
                                                            <a href="javascript:;" class="text-secondary" @click="showReminderModal">{{ $t('Elfelejtettem a jelszavamat') }}</a>
                                                        </div>
                                                    </div>
                                                    <div v-if="!hasUserAccount" class="col-12">
                                                        <p class="fs-6" v-html="$t('Ha még nincs miniBUD fiókja, kérjük, adja meg a kívánt felhasználói nevet és jelszót, majd kattintson a Következő gombra!')">
                                                        </p>
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" id="regUsername" name="username" :placeholder="$t('Felhasználói név')" :class="{ 'is-invalid': errors.username }" v-model="orderData.username" maxlength="150" autocomplete="username">
                                                            <label for="regUsername">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="regPassword" name="password" :placeholder="$t('Jelszó')" :class="{ 'is-invalid': errors.password }" v-model="orderData.password" maxlength="50" autocomplete="new-password">
                                                            <label for="regPassword">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="loginPasswordRepeat" :placeholder="$t('Jelszó ismét')" :class="{ 'is-invalid': errors.passwordRepeat }" v-model="orderData.passwordRepeat" maxlength="50" autocomplete="new-password">
                                                            <label for="loginPasswordRepeat">{{ $t('Jelszó ismét') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.passwordRepeat" class="invalid-feedback">{{ errors.passwordRepeat }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-if="user">
                                                    <p class="mb-3">{{ $t('Bejelentkezve, mint :name', { name: user.name }) }}</p>
                                                    <p class="mb-3"><a :href="route('logout')">{{ $t('Kijelentkezés') }}</a></p>
                                                </div>
                                                <button v-if="stepNrs.invoice" type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToInvoice">{{ $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Login or registration -->

                                    <!-- Invoice details -->
                                    <div class="accordion-item" v-if="stepNrs.invoice">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInvoice" aria-controls="collapseInvoice" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.login || !steps.extraProducts }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.invoice, 'text-success fa-check-circle': steps.invoice }"></i>{{
                                                        stepNrs.invoice }}. {{
                                                        $t('Számlázási adatok') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseInvoice" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <p class="fw-bold my-3">{{ $t('Vásárló') }}<span class="text-danger">*</span></p>
                                                        <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Vásárló')">
                                                            <input type="radio" class="btn-check" name="buyerType" id="company" autocomplete="off" :value="false" v-model="orderData.invoiceData.privatePerson">
                                                            <label class="btn btn-outline-secondary w-100" for="company"><i class="fal fa-building me-1"></i>
                                                                {{ $t('Cég') }}</label>
                                                            <input type="radio" class="btn-check" name="buyerType" id="private" autocomplete="off" :value="true" v-model="orderData.invoiceData.privatePerson">
                                                            <label class="btn btn-outline-secondary w-100" for="private"><i class="fal fa-user me-1"></i> {{
                                                                $t('Magánszemély') }}</label>
                                                            <div v-if="errors['invoiceData.privatePerson']" class="text-danger">{{ errors['invoiceData.privatePerson'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.name'] }" id="invName" :placeholder="$t(orderData.invoiceData.privatePerson ? 'Név' : 'Cégnév')" maxlength="150" v-model="orderData.invoiceData.name" />
                                                            <label for="invName">
                                                                <span v-if="orderData.invoiceData.privatePerson">{{ $t('Név') }}</span><span v-else>{{ $t('Cégnév') }}</span>
                                                                <span class="text-danger">*</span>
                                                            </label>
                                                            <div v-if="errors['invoiceData.name']" class="invalid-feedback">{{ errors['invoiceData.name'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <select class="form-select" :class="{ 'is-invalid': errors['invoiceData.country'] }" name="invCountry" id="invCountry" v-model="orderData.invoiceData.country">
                                                                <option v-for="c in countries" :value="$page.props.locale == 'hu' ? c.name_hu : c.name_en">{{
                                                                    $page.props.locale == 'hu' ? c.name_hu : c.name_en }}</option>
                                                            </select>
                                                            <label for="invCountry">{{ $t('Ország') }}</label>
                                                            <div v-if="errors['invoiceData.country']" class="invalid-feedback">{{ errors['invoiceData.country'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-4">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.postcode'] }" id="invPostcode" maxlength="10" :placeholder="$t('Irányítószám')" v-model="orderData.invoiceData.postcode" @change="cityFromPostcode" />
                                                            <label for="invPostcode">{{ $t('Irányítószám') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.postcode']" class="invalid-feedback">{{ errors['invoiceData.postcode'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-8">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.city'] }" id="invCity" maxlength="50" :placeholder="$t('Település')" v-model="orderData.invoiceData.city" @change="postcodeFromCity" />
                                                            <label for="invCity">{{ $t('Település') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.city']" class="invalid-feedback">{{ errors['invoiceData.city'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div :class="[orderData.invoiceData.privatePerson ? 'col-12' : 'col-md-8']">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.address'] }" id="invAddress" maxlength="150" :placeholder="$t('Cím (utca, házszám)')" v-model="orderData.invoiceData.address" />
                                                            <label for="invAddress">{{ $t('Cím (utca, házszám)') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.address']" class="invalid-feedback">{{ errors['invoiceData.address'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-4" v-if="!orderData.invoiceData.privatePerson">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.vatNr'] }" id="invVatNr" maxlength="20" :placeholder="$t('Adószám')" v-model="orderData.invoiceData.vatNr" />
                                                            <label for="invVatNr">{{ $t('Adószám') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.vatNr']" class="invalid-feedback">{{ errors['invoiceData.vatNr'] }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Invoice details -->

                                </div>

                                <hr />

                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-check mb-3">
                                            <AcceptTerms class="form-check-input" :class="{ 'is-invalid': errors.termsAccepted }" id="acceptTerms" v-model="orderData.termsAccepted" />
                                            <div v-if="errors.termsAccepted" class="invalid-feedback">{{ errors.termsAccepted }}</div>
                                        </div>
                                        <button type="button" v-if="user?.bankTransferAllowed" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="checkInvoiceDetails('transfer')">
                                            {{ $t('Rendelés elküldése') }}
                                        </button>
                                        <button type="button" v-else class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest || !steps.transferDetails" @click="checkInvoiceDetails('card')">
                                            {{ $t('Tovább a fizetéshez') }}
                                        </button>
                                    </div>
                                </div>

                            </form>

                        </section>
                    </main>

                </div>
            </div>

            <aside class="booking-sidebar-mobile d-block d-md-none fixed-bottom" ref="bookingSidebarMobile">
                <div class="card text-bg-secondary rounded-0 border-0 h-100" style="--bs-bg-opacity: .9;">
                    <div class="card-body h-100">
                        <div class="d-flex flex-column h-100">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <button type="button" class="btn btn-link text-white" @click="toggleMobileSidebar" :aria-label="$t('Részletek')"><i class="far fa-expand-alt"></i></button>
                                <div class="btn-group" role="group" :aria-label="$t('Pénznem')">
                                    <template v-for="c in currencies">
                                        <input type="radio" class="btn-check" name="currency" :id="'currency-' + c" autocomplete="off" :checked="orderData.currency == c" @click="orderData.currency = c">
                                        <label class="btn btn-xs btn-outline-primary text-white" :for="'currency-' + c">{{ getTotalPrice(c) }}</label>
                                    </template>
                                </div>
                                <div>&nbsp;</div>
                            </div>
                            <div class="overflow-auto">
                                <div class="cart-steps">
                                    <div class="cart-step">
                                        <ul class="step-list">
                                            <li class="step-list-item">
                                                <span>{{ $t('Típus') }}:</span>
                                                {{ $t(orderData.tripType == 'oneWay' ? 'Egyirányú transzfer' : 'Retúr transzfer') }}
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Transzfer') }}:</span>
                                                {{ $t(orderData.transferType == 'AC' ? 'Reptérről a városba' : 'Városból a reptérre') }}
                                            </li>
                                            <li class="step-list-item" v-if="orderData.selectedAddress?.placeId">
                                                <span>{{ $t('Cím') }}:</span>
                                                {{ orderData.selectedAddressText }}
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Utasok') }}:</span>
                                                <p class="m-0">{{ $t('Felnőtt utas') }}: {{ orderData.adults }}</p>
                                                <p class="m-0" v-if="orderData.children">{{ $t('2 éven aluli utas') }}: {{ orderData.children }}</p>
                                                <p class="m-0" v-if="orderData.childSeatCount">{{ $t('Gyerekülés') }}: {{ orderData.childSeatCount }}</p>
                                            </li>
                                            <li class="step-list-item">
                                                <span>{{ $t('Jármű típus') }}</span>
                                                <span v-if="orderData.carType == 'social'">{{ $t('Airport Minibusz') }}</span>
                                                <span v-if="orderData.carType == 'premium'">{{ $t('Premium Minibusz') }}</span>
                                                <span v-if="orderData.carType == 'vip'">{{ $t('VIP Minibusz') }}</span>
                                            </li>
                                            <li class="step-list-item" v-if="orderData.extraProducts.length">
                                                <span>{{ $t('Extra szolgáltatások') }}</span>
                                                <p class="m-0" v-for="product in orderData.extraProducts">{{ product.quantity }}x {{ getExtraProduct(product.id)?.name }}</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

        </div>

        <div id="reminderModal" class="modal fade" tabindex="-1">
            <div class="modal-dialog modal-fullscreen-md-down">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('Elfelejtett jelszó') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-12">
                                <p>{{ $t('Elfelejtette jelszavát? Adja meg a regisztrált felhasználói nevét vagy e-mail címét, és segítünk Önnek!') }}</p>
                            </div>
                            <div class="col-12">
                                <div class="form-floating mb-3">
                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.reminderusername }" id="reminderUsername" :placeholder="$t('Felhasználói név')" ref="reminderUsername">
                                    <label for="reminderUsername">{{ $t('Felhasználói név') }} / {{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                    <div v-if="errors.reminderusername" class="invalid-feedback">{{ errors.reminderusername }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('Mégsem') }}</button>
                        <button type="button" class="btn btn-primary" :disabled="ajaxRequest" @click="sendReminderMail">{{ $t('Küldés') }}</button>
                    </div>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
